import { faCamera, faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useRef, useState } from 'react';
import { uploadPhotoToImmich } from '@/api/immich';
import { useTripContext } from '@/contexts/TripContext';
import { ImmichImage } from '@/design-system/primitives/ImmichImage';
import { PhotoLightbox } from '@/design-system/primitives/PhotoLightbox';
import type { ImmichPhotoRef, Trip } from '@/types/trip';
import { twx } from '@/utils/tailwind';

export interface TripPhotosCornerProps {
  trip: Trip;
  /** When true, the user can attach new photos to the trip (trip-level)
   *  and remove existing photos (both trip- and place-level) from the
   *  lightbox. When false, the tile is read-only. */
  canEdit: boolean;
  /** Extra classes for the tile wrapper — typically `float-right ml-2`
   *  to slot the tile into a header row with content wrapping around it. */
  className?: string;
}

/** A photo with the origin needed to dispatch the right remove mutator
 *  and to build the lightbox caption. */
interface AggregatedPhoto {
  assetId: string;
  caption: string;
  originalFileName?: string;
  origin: { kind: 'trip' } | { kind: 'place'; placeId: string };
  /** Used for ordering: prefer EXIF timestamp, fall back to upload time. */
  sortKey: number;
}

const photoSortKey = (p: ImmichPhotoRef): number => {
  if (p.fileCreatedAt) {
    const t = new Date(p.fileCreatedAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return p.uploadedAt ?? 0;
};

/**
 * Aggregates a trip's full photo set — trip-level photos plus every
 * place's photos — into a single corner tile + lightbox. New uploads
 * triggered from here land as **trip-level** photos (no place
 * association). Removal dispatches to either `removePhotoFromTrip` or
 * `removePhotoFromPlace` depending on the photo's origin, so the
 * lightbox stays the unified manager.
 *
 * Lightbox caption format:
 *   - trip-level → "<TRIP NAME>"
 *   - place-level → "<TRIP NAME> – <PLACE NAME>"
 */
export const TripPhotosCorner = ({ trip, canEdit, className }: TripPhotosCornerProps) => {
  const { attachPhotoToTrip, removePhotoFromTrip, removePhotoFromPlace, setTripImmichAlbumId } =
    useTripContext();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Merge trip-level + every place's photos into a single timeline.
  // Newest first so the corner tile cover reflects the most recent
  // memory, and lightbox navigation feels chronological.
  const aggregated: Array<AggregatedPhoto> = useMemo(() => {
    const out: Array<AggregatedPhoto> = [];
    for (const p of trip.immichPhotos ?? []) {
      out.push({
        assetId: p.assetId,
        caption: trip.name,
        originalFileName: p.originalFileName,
        origin: { kind: 'trip' },
        sortKey: photoSortKey(p),
      });
    }
    for (const place of trip.places) {
      for (const p of place.immichPhotos ?? []) {
        out.push({
          assetId: p.assetId,
          caption: `${trip.name} – ${place.item.name}`,
          originalFileName: p.originalFileName,
          origin: { kind: 'place', placeId: place.item.id },
          sortKey: photoSortKey(p),
        });
      }
    }
    out.sort((a, b) => b.sortKey - a.sortKey);
    return out;
  }, [trip]);

  if (aggregated.length === 0 && !canEdit) return null;

  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadInProgress(true);
    setUploadError(null);
    try {
      // Sequential — first upload triggers lazy album-create on the
      // worker; subsequent ones reuse the returned id.
      let albumId = trip.immichAlbumId;
      // Same rationale as PlacePhotosCorner: ensure pre-added
      // collaborators get album access when the album is born on
      // this upload.
      // Only collaborators with a resolved Immich account get album access;
      // others still edit the trip, just without photo sync.
      const collaboratorUserIds = (trip.collaborators ?? []).flatMap((c) =>
        c.immichUserId ? [c.immichUserId] : [],
      );
      for (const file of Array.from(files)) {
        const result = await uploadPhotoToImmich({
          file,
          tripName: trip.name,
          tripLocation: trip.location,
          tripUrl: window.location.href,
          immichAlbumId: albumId,
          fileCreatedAt: file.lastModified ? new Date(file.lastModified).toISOString() : undefined,
          collaboratorUserIds,
        });
        if (result.albumCreated) {
          albumId = result.albumId;
          setTripImmichAlbumId(trip.id, result.albumId);
        }
        attachPhotoToTrip(trip.id, {
          assetId: result.assetId,
          originalFileName: result.info?.originalFileName,
          fileCreatedAt: result.info?.exifInfo?.dateTimeOriginal ?? result.info?.fileCreatedAt,
          width: result.info?.exifInfo?.exifImageWidth,
          height: result.info?.exifInfo?.exifImageHeight,
          uploadedAt: Date.now(),
        });
      }
    } catch (err) {
      console.error('[immich] upload failed', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadInProgress(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTileTap = () => {
    if (aggregated.length > 0) {
      setLightboxOpen(true);
    } else if (canEdit) {
      fileInputRef.current?.click();
    }
  };

  // Lightbox remove dispatcher — origin tells us which mutator to call.
  const handleRemove = (assetId: string) => {
    const target = aggregated.find((p) => p.assetId === assetId);
    if (!target) return;
    if (target.origin.kind === 'trip') {
      removePhotoFromTrip(trip.id, assetId);
    } else {
      removePhotoFromPlace(trip.id, target.origin.placeId, assetId);
    }
  };

  if (aggregated.length === 0) {
    return (
      <>
        <button
          type="button"
          onClick={handleTileTap}
          disabled={uploadInProgress}
          aria-label="Add trip photos"
          title="Add photos to this trip"
          className={twx(
            'block w-10 h-10 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors shrink-0',
            uploadInProgress
              ? 'border-content-border text-content-disabled cursor-wait'
              : 'border-content-border text-content-subtle hover:border-secondary-main hover:text-secondary-emphasized hover:bg-content-focal',
            className,
          )}
        >
          <FontAwesomeIcon
            icon={uploadInProgress ? faSpinnerThird : faCamera}
            spin={uploadInProgress}
            style={{ width: 16, height: 16 }}
          />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFilesPicked}
        />
        {uploadError && <p className="mt-1 text-[10px] text-error-text">{uploadError}</p>}
      </>
    );
  }

  const first = aggregated[0];
  const extra = aggregated.length - 1;
  return (
    <>
      <button
        type="button"
        onClick={handleTileTap}
        aria-label={`View ${aggregated.length} trip photo${aggregated.length === 1 ? '' : 's'}`}
        title={`View ${aggregated.length} trip photo${aggregated.length === 1 ? '' : 's'}`}
        className={twx(
          'relative w-10 h-10 rounded-lg overflow-hidden bg-content-focal shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-emphasized hover:opacity-90 transition-opacity',
          className,
        )}
      >
        {first && (
          <ImmichImage
            assetId={first.assetId}
            size="preview"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {extra > 0 && (
          <span
            aria-hidden
            className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center min-w-[16px] h-[14px] px-0.5 rounded-full bg-black/70 text-white text-[9px] font-semibold leading-none"
          >
            +{extra}
          </span>
        )}
        {uploadInProgress && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/40"
          >
            <FontAwesomeIcon
              icon={faSpinnerThird}
              spin={true}
              style={{ width: 16, height: 16, color: 'white' }}
            />
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFilesPicked}
      />
      {uploadError && <p className="mt-1 text-[10px] text-error-text">{uploadError}</p>}
      {lightboxOpen && (
        <PhotoLightbox
          photos={aggregated.map((p) => ({ assetId: p.assetId, caption: p.caption }))}
          onClose={() => setLightboxOpen(false)}
          onAddPhotos={canEdit ? () => fileInputRef.current?.click() : undefined}
          onRemovePhoto={canEdit ? handleRemove : undefined}
        />
      )}
    </>
  );
};
